'use client';

import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';
import type { User } from '@hummii/shared-types';

interface ProfileClientModalProps extends BaseModalProps {
  client?: User | null;
  stats?: {
    totalOrders?: number;
    activeOrders?: number;
    completedOrders?: number;
    memberSince?: Date;
  };
  onMessage?: (clientId: string) => void;
}

/**
 * Profile Client Modal - displays client profile information
 * Usage: const { open } = useModal('profile-client'); open({ client, stats });
 */
export function ProfileClientModal({
  isOpen,
  onClose,
  client,
  stats,
  onMessage,
}: ProfileClientModalProps) {
  if (!client) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        animation="slide-up"
        title="Client Profile"
      >
        <div className="text-center py-8">
          <p className="text-gray-500">Client information not available</p>
        </div>
      </Modal>
    );
  }

  const memberSince = stats?.memberSince || client.createdAt;
  const totalOrders = stats?.totalOrders || 0;
  const activeOrders = stats?.activeOrders || 0;
  const completedOrders = stats?.completedOrders || 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      animation="slide-up"
      title="Client Profile"
      showCloseButton
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
          {/* Avatar */}
          <div className="relative">
            {client.avatarUrl ? (
              <img
                src={client.avatarUrl}
                alt={client.name}
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {client.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            {/* Verified badge disabled (Identity Verification removed) */}
            {/* {client.isVerified && (
              <div
                className="absolute bottom-0 right-0 w-6 h-6 bg-blue-500 border-2 border-white rounded-full flex items-center justify-center"
                title="Verified"
              >
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )} */}
          </div>

          {/* Info */}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              {client.name}
            </h3>

            {/* Verified/Unverified status pill disabled (Identity Verification removed) */}
            {/* <div className="flex items-center gap-2 mb-2">
              {client.isVerified ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Verified Client
                </span>
              ) : (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                  Unverified
                </span>
              )}
            </div> */}

            <p className="text-sm text-gray-600">
              Member since {new Date(memberSince).toLocaleDateString('en-CA', {
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{totalOrders}</p>
            <p className="text-xs text-gray-600 mt-1">Total Orders</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{activeOrders}</p>
            <p className="text-xs text-gray-600 mt-1">Active Orders</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{completedOrders}</p>
            <p className="text-xs text-gray-600 mt-1">Completed</p>
          </div>
        </div>

        {/* Contact Information (optional - only if available) */}
        {client.phone && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              Contact Information
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-gray-700">{client.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="text-gray-700">{client.phone}</span>
              </div>
            </div>
          </div>
        )}

        {/* Activity Info */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">
            Account Activity
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Last Login:</span>
              <span className="text-gray-900">
                {client.lastLoginAt
                  ? new Date(client.lastLoginAt).toLocaleDateString('en-CA')
                  : 'Never'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Account Created:</span>
              <span className="text-gray-900">
                {new Date(client.createdAt).toLocaleDateString('en-CA')}
              </span>
            </div>
          </div>
        </div>

        {/* Roles/Badges */}
        {client.roles && client.roles.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Roles</h4>
            <div className="flex flex-wrap gap-2">
              {client.roles.map((role) => (
                <span
                  key={role}
                  className="px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full"
                >
                  {role}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          {onMessage && (
            <button
              type="button"
              onClick={() => onMessage(client.id)}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Send Message
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
